import { Box, Button, Dialog, Divider, IconButton, Paper, Slide, Typography } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { GridCloseIcon } from '@mui/x-data-grid';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plan, plans } from '../../interfaces/Plan';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* -------------------------------------- */

interface PlansModalProps {
  open: boolean;
  onClose: () => void;
  mode?: 'create' | 'edit';
  placeId?: number;
  currentPlanType?: string;
}

export default function PlansModal({
  open,
  onClose,
  mode = 'create',
  placeId,
  currentPlanType,
}: PlansModalProps) {
  const [selectedPlan, setSelectedPlan] = React.useState<Plan | null>(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!open) setSelectedPlan(null);
  }, [open]);

  const handleNext = () => {
    if (!selectedPlan) return;
    if (mode === 'edit' && placeId) {
      navigate(`/editar/espaco/${placeId}`, { state: { plan: selectedPlan } });
      return;
    }
    navigate('/cadastrar/espaco', { state: { plan: selectedPlan } });
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      {/* Close Button */}
      <Box display="flex" justifyContent="flex-end" p={3}>
        <IconButton onClick={onClose}>
          <GridCloseIcon />
        </IconButton>
      </Box>

      {/* Title */}
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        <Typography variant="h5" fontWeight="bold" mb={3}>
          {mode === 'edit'
            ? 'Selecione o plano desejado para continuar a edição'
            : 'Selecione o plano desejado'}
        </Typography>

        {/* Cards using Box instead of Grid */}
        <Box display="flex" flexWrap="wrap" gap={3} justifyContent="center">
          {plans.map((plan) => {
            const selected = selectedPlan?.id === plan.id;
            const isCurrent = !!currentPlanType && plan.type === currentPlanType;

            return (
              <Box key={plan.id} mt={3} width={{ xs: '100%', sm: 520 }}>
                <Paper
                  sx={{
                    p: 2.5,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    border: isCurrent
                      ? '2px solid #9e9e9e'
                      : selected
                      ? '2px solid #1976d2'
                      : '1px solid #e0e0e0',
                    boxShadow: selected ? '0 10px 30px rgba(25, 118, 210, 0.15)' : 'none',
                    transition: '0.25s',
                    cursor: isCurrent ? 'not-allowed' : 'pointer',
                    opacity: isCurrent ? 0.65 : 1,
                    '&:hover': isCurrent
                      ? {}
                      : {
                          borderColor: '#1976d2',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                        },
                  }}
                  onClick={() => !isCurrent && setSelectedPlan(plan)}
                >
                  <Box>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: selected ? 'primary.50' : 'grey.50',
                        border: '1px solid',
                        borderColor: selected ? 'primary.100' : 'grey.200',
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold">
                        {plan.name}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {plan.value}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      mt={1}
                      component="div"
                      sx={{
                        textAlign: 'left',
                        lineHeight: 1.6,
                        '& p': { margin: 0, marginBottom: 1.25 },
                        '& p:last-of-type': { marginBottom: 0 },
                      }}
                      dangerouslySetInnerHTML={{ __html: plan.description }}
                    />

                    <Divider sx={{ my: 2 }} />

                    <Box mt={1}>
                      <Typography fontWeight="bold">Benefícios:</Typography>
                      {plan.benefits.map((b, i) => (
                        <Typography key={i} variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                          {b}
                        </Typography>
                      ))}
                    </Box>
                  </Box>

                  <Box mt={2}>
                    <Button
                      fullWidth
                      disabled={isCurrent}
                      variant={isCurrent ? 'outlined' : selected ? 'contained' : 'outlined'}
                      color={isCurrent ? 'inherit' : 'primary'}
                    >
                      {isCurrent ? 'Plano Atual' : selected ? 'Selecionado' : 'Selecionar'}
                    </Button>
                  </Box>
                </Paper>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Footer */}
      <Box display="flex" justifyContent="flex-end" p={3}>
        <Button disabled={!selectedPlan} variant="contained" size="large" onClick={handleNext}>
          Próximo
        </Button>
      </Box>
    </Dialog>
  );
}
